import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, TextField, Grid} from 'views/components';
import {Translate, translate} from 'views/module';
import {LocationOn} from 'views/icons';

const Location = (props) => {
  const {newJob, updateJobLocation, setIsStepValid} = props;

  const location = newJob.location || {};
  const neighborhood = location.neighborhood || '';
  const city = location.city || '';

  // Update step validity when location data changes
  React.useEffect(() => {
    const isValid = neighborhood.trim() !== '' && city.trim() !== '';
    setIsStepValid(isValid);
  }, [neighborhood, city, setIsStepValid]);

  const handleNeighborhoodChange = (event) => {
    const value = event;
    updateJobLocation((location) => ({
      ...location,
      neighborhood: value,
    }));
  };

  const handleCityChange = (event) => {
    const value = event;
    updateJobLocation((location) => ({
      ...location,
      city: value,
    }));
  };

  // Create display text for preview using useMemo
  const previewText = React.useMemo(() => {
    const neighborhoodText = neighborhood.trim();
    const cityText = city.trim();

    if (neighborhoodText && cityText) {
      return `${neighborhoodText}, ${cityText}`;
    } else if (neighborhoodText) {
      return neighborhoodText;
    } else if (cityText) {
      return cityText;
    } else {
      return '';
    }
  }, [neighborhood, city]);

  return (
    <Box sx={{p: 2, maxWidth: 1200, mx: 'auto'}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.Location.Title" />
      </Typography>

      {/* Form Fields */}
      <Box sx={{maxWidth: 900, mx: 'auto', mb: 4}}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
              <Translate expression="career.create.Location.NeighborhoodLabel" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              <Translate expression="career.create.Location.NeighborhoodDescription" />
            </Typography>
            <TextField
              fullWidth
              value={neighborhood}
              onChange={handleNeighborhoodChange}
              placeholder={translate('career.create.Location.NeighborhoodPlaceholder')}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
              <Translate expression="career.create.Location.CityLabel" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              <Translate expression="career.create.Location.CityDescription" />
            </Typography>
            <TextField
              fullWidth
              value={city}
              onChange={handleCityChange}
              placeholder={translate('career.create.Location.CityPlaceholder')}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Location Preview */}
      <Box sx={{maxWidth: 900, mx: 'auto'}}>
        <Box
          sx={{
            p: 3,
            backgroundColor: '#f0f7ff',
            borderRadius: 2,
            border: '1px solid #e3f2fd',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <LocationOn sx={{color: '#1976d2', mt: 0.5}} />
          <Box>
            <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#1976d2', mb: 0.5}}>
              <Translate expression="career.create.Location.LocationPreviewTitle" />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {
                previewText && (
                  <Translate
                    expression="career.create.Location.LocationPreviewWithLocation"
                    substitute={{location: previewText}}
                  />
                )
              }
              {
                !previewText && (
                  <Translate expression="career.create.Location.LocationPreviewNoLocation" />
                )
              }
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

Location.propTypes = {
  newJob: PropTypes.object.isRequired,
  updateJobLocation: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
};

export default Location;
